import { useInfiniteQuery, QueryFunctionContext } from 'react-query';
import { gridFilters } from '@simplywallst/services';
import { companyMapper, getQueryKey } from './utils';
import type { Company } from './utils';
import { isValidGridRule } from '@/utilities/grid';

export interface Response {
  companies: Company[];
  meta: {
    totalPages: number;
    totalRecords: number;
  };
}

const getDefaultResponse = (): Response => ({
  meta: {
    totalRecords: 0,
    totalPages: 0,
  },
  companies: [],
});

type Include = 'info' | 'score' | 'analysis' | 'grid' | 'statements';

type Options = {
  pageSize?: number;
  rules?: string;
  pageQuery?: number;
  include?: Include[];
  isInfinite?: boolean;
  statementsFilter?: string[];
};

export function makeQueryFn(options: Options = {}) {
  const {
    rules,
    pageSize = 8,
    pageQuery = 1,
    isInfinite = false,
    include = ['grid', 'score'],
    statementsFilter = [],
  } = options;
  return async ({ pageParam = 1 }: QueryFunctionContext) => {
    if (typeof rules === 'undefined') return getDefaultResponse();
    const response = await gridFilters(
      {
        rules,
        offset: ((isInfinite ? pageParam : pageQuery) - 1) * pageSize,
        size: pageSize,
        state: 'read', // create | read
      },
      include.join()
    );
    // api/grid/filter can return a 204 when no companies exist within the filter
    // "if it hits the cache it returns a 200 with empty array in data.
    // but the first time the query runs, if it has no results, it returns 204...
    // [it] only runs when the cache is missed"
    if (response.status === 204) return getDefaultResponse();

    const { total_records: totalRecords } = response.data.meta;
    return {
      meta: {
        totalPages: Math.ceil((totalRecords || 0) / pageSize),
        totalRecords,
      },
      companies: response.data.data
        .map((n) => companyMapper(n, statementsFilter))
        .filter((n): n is Company => typeof n !== 'undefined'),
    };
  };
}

/**
 * ### Description
 *
 * This hook returns a list of companies from the grid (filter/screener) endpoint
 * based on a set of rules.
 *
 * ### Options
 *
 * - `rules` is a JSON string that the grid endpoint uses to filter a list of companies
 * - `pageSize` is the amount of companies you want return per page
 * - `pageQuery` is the page number (offset) that you want to load
 * - `isInfinite` determines whether the pages are returned in an infinite list, or separately based on the rules/pageQuery supplied
 * - `include` a list of the includes you want in the grid response. Defaults to ['grid', 'score']
 * - `statementsFilter` filters down the statements returned to those in the list only, plus the default risks and rewards which are always returned
 */
export const useGridCompanies = (options: Options = {}) => {
  const { rules, pageQuery = 1, isInfinite = false } = options;
  const {
    data = { pages: [getDefaultResponse()] },
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetched,
    status,
  } = useInfiniteQuery(
    getQueryKey(rules, pageQuery, isInfinite),
    makeQueryFn(options),
    {
      retry: false,
      staleTime: 3600000,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: isValidGridRule(rules),
      keepPreviousData: true,
      getNextPageParam: (lastPage, allPages) =>
        pageQuery < lastPage.meta.totalPages ? allPages.length + 1 : false,
    }
  );
  return {
    data,
    status,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetched,
  };
};
